import React, { useState, useEffect } from "react";
import { Form } from "react-bootstrap";
import Modal from "react-bootstrap/Modal";
import api, { BASE_URL } from "../../Utils/api";
import { Salvar, Cancelar } from "../Generals/buttons";
import ErrorAlert from "../../Utils/ErrorAlert";
import { Email } from "../Generals/Inputs";
import { useFeedback } from "../Generals/Feedback/FeedbackContext";
import { normalizarEmail, validarEmail } from "../../Utils/validarEmail";


function AdicionarUsuarioEntidadeModal({ show, onClose, entidade }) {
  const { toast, confirm } = useFeedback();
  const [email, setEmail] = useState("");
  const [idGrupoUsuario, setIdGrupoUsuario] = useState("");
  const [grupos, setGrupos] = useState([]);
  const [error, setError] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const token = localStorage.getItem("authToken");
  const entidadeId = entidade?.id;

  useEffect(() => {
    if (show) {
      setEmail("");
      setIdGrupoUsuario("");
      setError(null);
    }
  }, [show]);

  // Item #27A: grupo é por entidade — busca os grupos da entidade de destino
  // (não da entidade ativa da sessão), mesmo padrão de UsuariosModal.js.
  useEffect(() => {
    if (!show || !entidadeId) return;
    const headers = { Authorization: `Bearer ${token}` };
    api
      .get(`${BASE_URL}/api/gruposusuario/ListarGruposUsuario/${entidadeId}`, { headers })
      .then((res) => {
        const lista = Array.isArray(res.data?.data) ? res.data.data : [];
        lista.sort((a, b) => (a.descricao || "").localeCompare(b.descricao || ""));
        setGrupos(lista);
      })
      .catch((err) => {
        console.error("Erro ao listar grupos:", err);
      });
  }, [show, entidadeId, token]);

  // Item #27B: quando não existe usuário ativo com este e-mail, oferece convidar
  // um novo usuário (cria conta via link de convite) em vez de apenas falhar.
  const ofertarConviteNovoUsuario = async (emailNorm) => {
    if (!idGrupoUsuario) {
      setError({
        message:
          "Nenhum usuário ativo encontrado com este e-mail. Selecione um grupo de permissões e tente novamente para convidar um novo usuário.",
      });
      return;
    }

    const confirmado = await confirm(
      `Nenhum usuário ativo encontrado com este e-mail. Deseja convidar ${emailNorm} para esta entidade?`,
      "Convidar novo usuário",
      "warning"
    );
    if (!confirmado) return;

    setSalvando(true);
    try {
      const res = await api.post(
        `${BASE_URL}/api/usuarios/ConvidarUsuarioEntidade`,
        {
          email: emailNorm,
          id_entidade: entidadeId,
          id_grupo_usuario: idGrupoUsuario,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data?.message || "Convite enviado com sucesso.");
      onClose();
    } catch (err) {
      setError({ message: err?.response?.data?.message || "Erro ao enviar convite." });
    } finally {
      setSalvando(false);
    }
  };

  const handleSalvar = async () => {
    setError(null);
    const emailNorm = normalizarEmail(email);
    if (!emailNorm) {
      setError({ message: "Informe o e-mail do usuário." });
      return;
    }
    if (!validarEmail(emailNorm)) {
      setError({ message: "E-mail inválido." });
      return;
    }

    setSalvando(true);
    try {
      const res = await api.post(
        `${BASE_URL}/api/usuarios/AdicionarUsuarioEntidade`,
        {
          email: emailNorm,
          id_entidade: entidadeId,
          id_grupo_usuario: idGrupoUsuario || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data?.message || "Usuário adicionado com sucesso.");
      onClose();
    } catch (err) {
      const mensagem = err?.response?.data?.message;
      if (err?.response?.status === 404 && mensagem === "Nenhum usuário ativo encontrado com este e-mail.") {
        setSalvando(false);
        await ofertarConviteNovoUsuario(emailNorm);
        return;
      }
      setError({ message: mensagem || "Erro ao adicionar usuário." });
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      backdrop="static"
      animation={false}
      dialogClassName="modal-dialog-top"
    >
      <Modal.Header closeButton style={{ background: "var(--surface-sunken)", borderColor: "var(--border-default)", paddingBottom: 12 }}>
        <Modal.Title style={{ fontSize: "1rem", fontWeight: 600, color: "var(--text-body)" }}>
          Adicionar Usuário — {entidade?.fantasia || entidade?.razao_social || "Entidade"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ background: "var(--surface-card)", color: "var(--text-body)" }}>
        <ErrorAlert error={error} />
        <Form.Group controlId="emailUsuario" className="mt-2">
          <Form.Label>E-mail do usuário <span className="text-danger">*</span></Form.Label>
          <Email
            value={email}
            onChange={setEmail}
            placeholder="usuario@empresa.com.br"
            required
          />
          <Form.Text className="text-muted">
            O usuário deve estar cadastrado e ativo no sistema.
          </Form.Text>
        </Form.Group>

        <Form.Group controlId="grupoUsuarioEntidade" className="mt-3">
          <Form.Label>Grupo de Permissões</Form.Label>
          <Form.Select
            value={idGrupoUsuario}
            onChange={(e) => setIdGrupoUsuario(e.target.value)}
          >
            <option value="">Selecione um grupo</option>
            {grupos.map((gr) => (
              <option key={gr.id} value={gr.id}>
                {gr.descricao}
              </option>
            ))}
          </Form.Select>
          <Form.Text className="text-muted">
            Obrigatório para usuários do tipo Normal; ignorado para
            Administrador/Comunicado.
          </Form.Text>
        </Form.Group>
      </Modal.Body>

      <Modal.Footer className="modal-footer-padrao">
        <div className="modal-footer-acoes-esquerda" />
        <div className="modal-footer-acoes-direita">
          <Cancelar onClick={onClose} />
          <Salvar onClick={handleSalvar} disabled={salvando} />
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default AdicionarUsuarioEntidadeModal;
